import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Search, Loader2, CheckCircle2, AlertTriangle, XCircle,
  Sparkles, Globe, ListOrdered, RefreshCw, Wrench, ArrowRight, Trophy, FileText, Calendar,
  ExternalLink, Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import ModuleDemoVideo from '../components/ModuleDemoVideo';

function apiBase() {
  const env = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  if (env) return env;
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') return 'http://127.0.0.1:8002';
    return window.location.origin.replace(/\/$/, '');
  }
  return 'http://127.0.0.1:8002';
}

const API = apiBase();

const PHASE_LABELS = {
  queued: 'Queued',
  discovering: 'Discovering site',
  crawling: 'Crawling pages',
  analyzing: 'Analyzing SEO signals',
  planning: 'Building action plan',
  researching: 'Keyword + SERP research',
  content: 'Drafting ranking content',
  applying: 'Applying safe fixes',
  verifying: 'Re-auditing after apply',
  done: 'Complete',
  failed: 'Failed',
};

const SEVERITY_TONE = {
  critical: 'text-rose-300 bg-rose-500/15 border-rose-500/30',
  high: 'text-amber-300 bg-amber-500/15 border-amber-500/30',
  medium: 'text-sky-300 bg-sky-500/15 border-sky-500/30',
  low: 'text-zinc-300 bg-zinc-500/15 border-zinc-500/30',
};

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

const AiSeoDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [url, setUrl] = useState('');
  const [goal, setGoal] = useState('');
  const [websiteId, setWebsiteId] = useState('');
  const [websites, setWebsites] = useState([]);
  const [startCampaign, setStartCampaign] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [publishingIdx, setPublishingIdx] = useState(null);
  const [activeRun, setActiveRun] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const pollRef = useRef(null);
  const deepLinkRunHandled = useRef(false);

  const loadRuns = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/api/ai-seo/runs?limit=15`, { headers: authHeaders() });
      if (!resp.ok) throw new Error('Could not load SEO runs');
      const data = await resp.json();
      setRuns(data.runs || []);
    } catch (err) {
      setRuns([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadWebsites = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/api/ai-seo/websites`, { headers: authHeaders() });
      if (!resp.ok) return;
      const data = await resp.json();
      setWebsites(data.websites || []);
    } catch {
      setWebsites([]);
    }
  }, []);

  const loadCampaigns = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/api/ai-seo/campaigns?limit=10`, { headers: authHeaders() });
      if (!resp.ok) return;
      const data = await resp.json();
      setCampaigns(data.campaigns || []);
    } catch {
      setCampaigns([]);
    }
  }, []);

  useEffect(() => {
    loadRuns();
    loadWebsites();
    loadCampaigns();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadRuns, loadWebsites, loadCampaigns]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const pollRun = (runId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const resp = await fetch(`${API}/api/ai-seo/runs/${runId}`, { headers: authHeaders() });
        if (!resp.ok) return;
        const data = await resp.json();
        const run = data.run;
        setActiveRun(run);
        if (run.status === 'done' || run.status === 'failed') {
          stopPolling();
          setSubmitting(false);
          setApplying(false);
          loadRuns();
          if (run.status === 'done') {
            loadCampaigns();
            if (run.campaign_id) {
              openCampaign(run.campaign_id).catch(() => {});
            }
            if (run.score_after != null && run.score_before != null && run.apply_result) {
              toast.success(`Score ${run.score_before} → ${run.score_after}`);
            } else {
              toast.success(`SEO score: ${run.score ?? '—'}/100`);
            }
          } else toast.error(run.error || 'SEO agent failed');
        }
      } catch {
        /* keep polling */
      }
    }, 1500);
  };

  const startAudit = async (e) => {
    e.preventDefault();
    if (!url.trim() && !websiteId) {
      toast.error('Enter a website URL or pick a Snagcart site');
      return;
    }
    setSubmitting(true);
    setActiveRun(null);
    try {
      const resp = await fetch(`${API}/api/ai-seo/runs`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          url: url.trim() || undefined,
          goal: goal.trim(),
          website_id: websiteId || undefined,
          start_campaign: startCampaign,
          campaign_days: 30,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = data.detail;
        const msg = typeof detail === 'object' ? detail.message || detail.detail : detail;
        throw new Error(msg || 'Failed to start SEO agent');
      }
      setActiveRun(data.run);
      pollRun(data.run.id);
      toast.message(startCampaign
        ? 'SEO sprint started — audit, rankings research, and 30-day monitoring…'
        : 'SEO agent started — crawling your site…');
    } catch (err) {
      setSubmitting(false);
      toast.error(err.message || 'Failed to start');
    }
  };

  const openRun = async (runId) => {
    try {
      const resp = await fetch(`${API}/api/ai-seo/runs/${runId}`, { headers: authHeaders() });
      if (!resp.ok) throw new Error('Run not found');
      const data = await resp.json();
      setActiveRun(data.run);
      if (data.run.status === 'running' || data.run.status === 'queued') {
        setSubmitting(true);
        pollRun(runId);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    const runId = searchParams.get('run');
    if (!runId || deepLinkRunHandled.current || loadingList) return;
    deepLinkRunHandled.current = true;
    openRun(runId);
  }, [searchParams, loadingList]);

  const applyFixes = async () => {
    if (!activeRun?.id) return;
    setApplying(true);
    setSubmitting(true);
    try {
      const resp = await fetch(`${API}/api/ai-seo/runs/${activeRun.id}/apply`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({}),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = data.detail;
        throw new Error(typeof detail === 'object' ? detail.message || JSON.stringify(detail) : (detail || 'Apply failed'));
      }
      setActiveRun(data.run);
      if (data.run.status === 'running' || ['applying', 'verifying'].includes(data.run.phase)) {
        pollRun(data.run.id);
      } else {
        setApplying(false);
        setSubmitting(false);
        loadRuns();
        if (data.run.score_after != null) {
          toast.success(`Applied fixes — score ${data.run.score_before ?? '—'} → ${data.run.score_after}`);
        }
      }
    } catch (err) {
      setApplying(false);
      setSubmitting(false);
      toast.error(err.message || 'Apply failed');
    }
  };

  const onPickWebsite = (id) => {
    setWebsiteId(id);
    if (!id) {
      return;
    }
    const site = websites.find((w) => w.id === id);
    if (!site) return;
    if (site.can_audit === false) {
      setUrl('');
      toast.error(site.audit_blocked_reason || 'Publish this site or attach a domain before auditing');
      return;
    }
    if (site.crawl_url) {
      setUrl(site.crawl_url);
      return;
    }
    const domain = (site.custom_domain || '').replace(/^https?:\/\//i, '').replace(/\/$/, '');
    if (domain) {
      setUrl(`https://${domain}/`);
      return;
    }
    if (site.slug) {
      setUrl(`${API}/api/sites/${site.slug}/html`);
      return;
    }
    setUrl('');
    toast.error('This site has no public URL yet. Publish it or attach a domain.');
  };

  const openCampaign = async (id) => {
    try {
      const resp = await fetch(`${API}/api/ai-seo/campaigns/${id}`, { headers: authHeaders() });
      if (!resp.ok) throw new Error('Campaign not found');
      const data = await resp.json();
      setActiveCampaign(data.campaign);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const runCampaignNow = async (id) => {
    try {
      toast.message('Running campaign check…');
      const resp = await fetch(`${API}/api/ai-seo/campaigns/${id}/run-now`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || 'Campaign check failed');
      setActiveCampaign(data.campaign);
      loadCampaigns();
      toast.success(`Campaign score: ${data.campaign?.latest_score ?? '—'}`);
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  };

  const publishDraft = async (draftIndex) => {
    if (!activeRun?.id) {
      toast.error('Run the SEO agent on a Snagcart site first');
      return;
    }
    if (!activeRun?.website_id && !activeRun?.matched_website) {
      toast.error('Publish works only for your Snagcart sites — pick one from the dropdown');
      return;
    }
    setPublishingIdx(draftIndex);
    try {
      const resp = await fetch(`${API}/api/ai-seo/runs/${activeRun.id}/publish-draft`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          draft_index: draftIndex,
          campaign_id: activeRun.campaign_id || activeCampaign?.id || undefined,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = data.detail;
        throw new Error(typeof detail === 'object' ? detail.message || JSON.stringify(detail) : (detail || 'Publish failed'));
      }
      toast.success(data.message || 'Published as a new page');
      // refresh run so draft shows published state
      const runResp = await fetch(`${API}/api/ai-seo/runs/${activeRun.id}`, { headers: authHeaders() });
      if (runResp.ok) {
        const runData = await runResp.json();
        setActiveRun(runData.run);
      }
      if (data.preview_url) {
        // leave navigation optional; show toast with link text
        toast.message(`Preview: ${data.page?.slug || 'new page'}`);
      }
    } catch (err) {
      toast.error(err.message || 'Publish failed');
    } finally {
      setPublishingIdx(null);
    }
  };

  const findings = activeRun?.audit_after?.findings || activeRun?.audit?.findings || [];
  const plan = activeRun?.plan;
  const serp = activeRun?.serp || activeCampaign?.latest_serp;
  const contentPack = activeRun?.content || activeCampaign?.latest_content;
  const phase = activeRun?.phase || '';
  const isBusy = submitting || applying || ['queued', 'running'].includes(activeRun?.status) ||
    ['discovering', 'crawling', 'analyzing', 'planning', 'researching', 'content', 'applying', 'verifying'].includes(phase);
  const isSnagcartSite = Boolean(activeRun?.matched_website || activeRun?.website_id || activeRun?.plan?.can_apply);
  const canApply = activeRun?.status === 'done' && isSnagcartSite;
  const alreadyApplied = Boolean(activeRun?.apply_result);
  const canPublishDrafts = Boolean(
    activeRun?.status === 'done' && (activeRun?.matched_website || activeRun?.website_id)
  );

  return (
    <div className="min-h-screen md:pl-64 p-6 md:p-10 bg-zinc-950 text-white" data-testid="ai-seo-page">
      <header className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/marketing-suite')}
            className="p-2 rounded-full hover:bg-white/5 text-zinc-400"
            data-testid="ai-seo-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
              <Sparkles className="w-7 h-7 text-emerald-400" />
              AI SEO Agent
            </h1>
            <p className="text-zinc-400 text-sm">
              Paste a URL. We audit, research rankings, draft content, and can run a 30-day sprint.
              No tool can guarantee #1 — we push you toward competing for it.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={loadRuns}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-medium border border-zinc-700 text-zinc-300 hover:border-zinc-600"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </header>

      <ModuleDemoVideo moduleKey="ai-seo" className="mb-6" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <form
            onSubmit={startAudit}
            className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 space-y-4"
            data-testid="ai-seo-form"
          >
            {websites.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-1">Your Snagcart site (optional)</label>
                <select
                  value={websiteId}
                  onChange={(e) => onPickWebsite(e.target.value)}
                  disabled={isBusy}
                  className="w-full h-10 px-3 rounded-lg bg-zinc-950 border border-zinc-700 text-sm"
                  data-testid="ai-seo-website"
                >
                  <option value="">External URL / paste below</option>
                  {websites.map((w) => (
                    <option key={w.id} value={w.id} disabled={w.can_audit === false}>
                      {w.name || w.slug || 'Untitled'}
                      {w.is_published ? '' : ' (draft)'}
                      {w.can_audit === false ? ' — publish/domain needed' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Website URL</label>
              <div className="relative">
                <Globe className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://yoursite.com"
                  className="w-full h-10 pl-9 pr-3 rounded-lg bg-zinc-950 border border-zinc-700 text-sm focus:outline-none focus:border-emerald-500/50"
                  data-testid="ai-seo-url"
                  disabled={isBusy}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Goal / target keywords (optional)</label>
              <input
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder="e.g. Rank for local dentist searches"
                className="w-full h-10 px-3 rounded-lg bg-zinc-950 border border-zinc-700 text-sm focus:outline-none focus:border-emerald-500/50"
                data-testid="ai-seo-goal"
                disabled={isBusy}
              />
            </div>
            <label className="flex items-start gap-2 text-xs text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={startCampaign}
                onChange={(e) => setStartCampaign(e.target.checked)}
                disabled={isBusy}
                className="mt-0.5"
                data-testid="ai-seo-campaign"
              />
              <span>
                Start <strong className="text-emerald-200">30-day SEO sprint</strong> —
                re-audit every ~3 days, track score/SERP, auto-apply safe fixes on Snagcart sites.
              </span>
            </label>
            <button
              type="submit"
              disabled={isBusy}
              data-testid="ai-seo-start"
              className="w-full h-10 rounded-lg text-sm font-medium bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {isBusy ? 'Agent running…' : (startCampaign ? 'Start 30-day SEO sprint' : 'Run SEO agent')}
            </button>
          </form>

          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="ai-seo-campaigns">
            <h3 className="text-sm font-semibold mb-3 inline-flex items-center gap-2">
              <Calendar className="w-4 h-4 text-emerald-400" /> 30-day sprints
            </h3>
            {campaigns.length === 0 ? (
              <p className="text-xs text-zinc-500">No active sprints yet.</p>
            ) : (
              <ul className="space-y-2">
                {campaigns.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => openCampaign(c.id)}
                      className={`w-full text-left rounded-lg border px-3 py-2 text-xs ${
                        activeCampaign?.id === c.id
                          ? 'border-emerald-500/40 bg-emerald-500/10'
                          : 'border-zinc-800 hover:border-zinc-700'
                      }`}
                    >
                      <div className="truncate font-medium">{c.url}</div>
                      <div className="flex justify-between text-zinc-500 mt-1">
                        <span>{c.status}</span>
                        <span>
                          {c.baseline_score != null ? `${c.baseline_score}` : '—'}
                          {' → '}
                          {c.latest_score != null ? `${c.latest_score}` : '—'}
                          {c.best_serp_position ? ` · #${c.best_serp_position}` : ''}
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="ai-seo-history">
            <h3 className="text-sm font-semibold mb-3">Recent runs</h3>
            {loadingList ? (
              <p className="text-xs text-zinc-500 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
              </p>
            ) : runs.length === 0 ? (
              <p className="text-xs text-zinc-500">No audits yet. Paste a URL to start.</p>
            ) : (
              <ul className="space-y-2">
                {runs.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => openRun(r.id)}
                      className={`w-full text-left rounded-lg border px-3 py-2 text-xs transition-colors ${
                        activeRun?.id === r.id
                          ? 'border-emerald-500/40 bg-emerald-500/10'
                          : 'border-zinc-800 hover:border-zinc-700 bg-zinc-950/40'
                      }`}
                    >
                      <div className="truncate font-medium text-zinc-200">{r.url}</div>
                      <div className="flex items-center justify-between mt-1 text-zinc-500">
                        <span>{PHASE_LABELS[r.phase] || r.status}</span>
                        <span>{r.score != null ? `${r.score}/100` : '—'}</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          {!activeRun ? (
            <div className="rounded-2xl border border-dashed border-zinc-700 bg-zinc-900/40 p-10 text-center text-sm text-zinc-500">
              Results appear here after you run the agent.
            </div>
          ) : (
            <>
              <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="ai-seo-status">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">Target</div>
                    <div className="text-sm font-medium truncate max-w-xl">{activeRun.url}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    {activeRun.score_before != null && activeRun.score_after != null ? (
                      <div className="text-right">
                        <div className="text-xs text-zinc-500">Before → After</div>
                        <div className="text-xl font-bold text-emerald-300 inline-flex items-center gap-1.5">
                          {activeRun.score_before} <ArrowRight className="w-4 h-4 text-zinc-500" /> {activeRun.score_after}
                        </div>
                      </div>
                    ) : activeRun.score != null ? (
                      <div className="text-right">
                        <div className="text-xs text-zinc-500">Score</div>
                        <div className="text-2xl font-bold text-emerald-300">{activeRun.score}</div>
                      </div>
                    ) : null}
                    <span className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs border ${
                      activeRun.status === 'done'
                        ? 'border-emerald-500/40 text-emerald-200 bg-emerald-500/10'
                        : activeRun.status === 'failed'
                          ? 'border-rose-500/40 text-rose-200 bg-rose-500/10'
                          : 'border-sky-500/40 text-sky-200 bg-sky-500/10'
                    }`}>
                      {isBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      {activeRun.status === 'done' && <CheckCircle2 className="w-3.5 h-3.5" />}
                      {activeRun.status === 'failed' && <XCircle className="w-3.5 h-3.5" />}
                      {PHASE_LABELS[activeRun.phase] || activeRun.status}
                    </span>
                  </div>
                </div>
                {activeRun.matched_website && (
                  <p className="text-xs text-emerald-300/90 mb-3">
                    Matched Snagcart site: {activeRun.matched_website.name || activeRun.matched_website.slug}
                    {activeRun.matched_website.is_published ? '' : ' (publish to serve robots/llms publicly)'}
                  </p>
                )}
                {(activeRun.phases || []).length > 0 && (
                  <ol className="flex flex-wrap gap-2">
                    {(activeRun.phases || []).map((p, i) => (
                      <li
                        key={`${p.phase}-${i}`}
                        className="text-[11px] px-2 py-1 rounded-md bg-zinc-950 border border-zinc-800 text-zinc-400"
                      >
                        {PHASE_LABELS[p.phase] || p.phase}
                      </li>
                    ))}
                  </ol>
                )}

                {activeRun.status === 'done' && (
                  <div
                    className={`mt-4 rounded-xl border p-4 ${
                      canApply
                        ? 'border-emerald-500/40 bg-emerald-500/10'
                        : 'border-zinc-700 bg-zinc-950/50'
                    }`}
                    data-testid="ai-seo-apply-panel"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-white inline-flex items-center gap-2">
                          <Wrench className="w-4 h-4 text-emerald-300" />
                          Apply safe SEO fixes
                        </div>
                        <p className="text-xs text-zinc-400 mt-1">
                          {canApply
                            ? 'One click updates your Snagcart site (title, meta, robots, llms.txt, schema, sitemap) and re-audits.'
                            : 'Available only for your Snagcart sites. Pick a site from the dropdown above (not an external URL), then run the agent again.'}
                        </p>
                        {activeRun.campaign_id && (
                          <p className="text-[11px] text-emerald-200/80 mt-1">
                            30-day sprint is on — safe fixes also auto-apply on scheduled checks.
                          </p>
                        )}
                      </div>
                      {canApply ? (
                        <button
                          type="button"
                          onClick={applyFixes}
                          disabled={applying || isBusy}
                          data-testid="ai-seo-apply"
                          className="shrink-0 inline-flex items-center gap-2 h-11 px-5 rounded-lg text-sm font-semibold bg-emerald-500 text-zinc-950 hover:bg-emerald-400 disabled:opacity-50"
                        >
                          {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
                          {alreadyApplied ? 'Apply again & re-audit' : 'Apply safe fixes & re-audit'}
                        </button>
                      ) : null}
                    </div>
                    {alreadyApplied && (
                      <div className="mt-3 pt-3 border-t border-emerald-500/20 text-xs text-emerald-100" data-testid="ai-seo-apply-result">
                        Last apply: {(activeRun.apply_result.applied || []).length} fix(es)
                        {(activeRun.apply_result.applied || []).length > 0 && (
                          <ul className="mt-2 space-y-1 text-emerald-200/90">
                            {activeRun.apply_result.applied.map((a) => (
                              <li key={`${a.code}-${a.field}`}>• {a.note || a.code}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {activeRun.error ? (
                  <p className="mt-3 text-sm text-rose-300">{activeRun.error}</p>
                ) : null}
              </div>

              {activeCampaign && (
                <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5" data-testid="ai-seo-campaign-panel">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h3 className="text-base font-semibold inline-flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-emerald-400" /> 30-day sprint
                    </h3>
                    {activeCampaign.status === 'active' && (
                      <button
                        type="button"
                        onClick={() => runCampaignNow(activeCampaign.id)}
                        className="h-8 px-3 rounded-lg text-xs border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/15"
                      >
                        Run check now
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-zinc-400 mb-3 truncate">{activeCampaign.url}</p>
                  <div className="grid grid-cols-3 gap-2 text-center text-xs mb-3">
                    <div className="rounded-lg border border-zinc-800 p-2">
                      <div className="text-zinc-500">Baseline</div>
                      <div className="text-lg font-semibold text-zinc-200">{activeCampaign.baseline_score ?? '—'}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 p-2">
                      <div className="text-zinc-500">Latest</div>
                      <div className="text-lg font-semibold text-emerald-300">{activeCampaign.latest_score ?? '—'}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 p-2">
                      <div className="text-zinc-500">Best SERP</div>
                      <div className="text-lg font-semibold text-amber-300">
                        {activeCampaign.best_serp_position ? `#${activeCampaign.best_serp_position}` : 'Not top 10'}
                      </div>
                    </div>
                  </div>
                  {(activeCampaign.history || []).length > 0 && (
                    <ul className="space-y-1 text-[11px] text-zinc-500 max-h-28 overflow-auto">
                      {[...(activeCampaign.history || [])].reverse().slice(0, 8).map((h) => (
                        <li key={h.at + String(h.run_id)}>
                          {new Date(h.at).toLocaleString()} — score {h.score ?? '—'}
                          {h.best_serp_position ? ` · #${h.best_serp_position}` : ''}
                          {(h.applied || []).length ? ` · applied ${h.applied.length}` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {serp && (
                <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="ai-seo-serp">
                  <h3 className="text-base font-semibold inline-flex items-center gap-2 mb-2">
                    <Trophy className="w-4 h-4 text-amber-400" /> Rankings reality check
                  </h3>
                  <p className="text-sm text-zinc-300 mb-3">{serp.summary}</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(serp.keywords || []).map((k) => (
                      <span key={k} className="text-xs px-2 py-1 rounded-md bg-zinc-950 border border-zinc-700">{k}</span>
                    ))}
                  </div>
                  <ul className="space-y-2">
                    {(serp.snapshots || []).slice(0, 5).map((s) => (
                      <li key={s.keyword} className="rounded-lg border border-zinc-800 px-3 py-2 text-xs">
                        <div className="flex justify-between gap-2">
                          <span className="font-medium text-zinc-200">{s.keyword}</span>
                          <span className={s.target_position ? 'text-emerald-300' : 'text-zinc-500'}>
                            {s.target_position ? `You: #${s.target_position}` : (s.available ? 'Not in top 10' : 'SERP offline')}
                          </span>
                        </div>
                        {(s.competitors || []).slice(0, 2).map((c) => (
                          <div key={c.link} className="text-zinc-500 mt-1 truncate">
                            #{c.position} {c.title}
                          </div>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {contentPack?.drafts?.length > 0 && (
                <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="ai-seo-content">
                  <h3 className="text-base font-semibold inline-flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-sky-400" /> Content to climb rankings
                  </h3>
                  <p className="text-xs text-zinc-500 mb-4">
                    {canPublishDrafts
                      ? 'Review each draft, then publish it as a new page on your Snagcart site (shows in nav). External sites: copy the draft manually.'
                      : 'Copy these drafts into your site. To one-click publish, run the agent on a Snagcart site from the dropdown.'}
                  </p>
                  <div className="space-y-4">
                    {contentPack.drafts.map((d, idx) => (
                      <article key={`${d.type}-${d.title}-${idx}`} className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                          <div className="text-[10px] uppercase tracking-wider text-sky-300/80">{d.type} · {d.keyword}</div>
                          {d.published_page_id ? (
                            <span className="text-[10px] px-2 py-0.5 rounded border border-emerald-500/40 text-emerald-200 bg-emerald-500/10">
                              Published{d.published_slug ? ` · /${d.published_slug}` : ''}
                            </span>
                          ) : null}
                        </div>
                        <h4 className="text-sm font-semibold mb-1">{d.title}</h4>
                        <p className="text-xs text-zinc-400 mb-2">{d.meta_description}</p>
                        {(d.outline || []).length > 0 && (
                          <ul className="text-xs text-zinc-500 mb-2 list-disc pl-4">
                            {d.outline.slice(0, 6).map((o) => <li key={o}>{o}</li>)}
                          </ul>
                        )}
                        <pre className="text-[11px] text-zinc-300 whitespace-pre-wrap max-h-48 overflow-auto rounded-lg bg-zinc-950 border border-zinc-800 p-3">
                          {(d.body || '').slice(0, 2500)}
                        </pre>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {canPublishDrafts && !d.published_page_id && (
                            <button
                              type="button"
                              onClick={() => publishDraft(idx)}
                              disabled={publishingIdx === idx}
                              data-testid={`ai-seo-publish-draft-${idx}`}
                              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold bg-sky-500 text-zinc-950 hover:bg-sky-400 disabled:opacity-50"
                            >
                              {publishingIdx === idx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                              Publish to my site
                            </button>
                          )}
                          {d.preview_url && (
                            <a
                              href={d.preview_url.startsWith('http') ? d.preview_url : `${API}${d.preview_url}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs border border-zinc-700 text-zinc-300 hover:border-zinc-500"
                            >
                              <ExternalLink className="w-3.5 h-3.5" /> View page
                            </a>
                          )}
                          {d.published_page_id && activeRun?.website_id && (
                            <button
                              type="button"
                              onClick={() => navigate(`/edit/${activeRun.website_id}`)}
                              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs border border-zinc-700 text-zinc-300 hover:border-zinc-500"
                            >
                              Open editor
                            </button>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              )}

              {plan && (
                <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="ai-seo-plan">
                  <h3 className="text-base font-semibold inline-flex items-center gap-2 mb-2">
                    <ListOrdered className="w-4 h-4 text-emerald-400" /> Action plan
                  </h3>
                  <p className="text-sm text-zinc-300 mb-4">{plan.summary}</p>
                  {(plan.quick_wins || []).length > 0 && (
                    <div className="mb-4">
                      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Quick wins</div>
                      <ul className="flex flex-wrap gap-2">
                        {plan.quick_wins.map((w) => (
                          <li key={w} className="text-xs px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-200">
                            {w}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <ol className="space-y-3">
                    {(plan.actions || []).map((a) => (
                      <li key={`${a.priority}-${a.title}`} className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-medium">
                              <span className="text-zinc-500 mr-2">#{a.priority}</span>
                              {a.title}
                            </div>
                            {a.why ? <p className="text-xs text-zinc-400 mt-1">{a.why}</p> : null}
                            {a.how ? <p className="text-xs text-zinc-300 mt-2"><span className="text-zinc-500">How: </span>{a.how}</p> : null}
                            {a.acceptance ? <p className="text-[11px] text-zinc-500 mt-2">Done when: {a.acceptance}</p> : null}
                          </div>
                          <span className={`shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${SEVERITY_TONE[a.severity] || SEVERITY_TONE.medium}`}>
                            {a.severity}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ol>
                  <p className="text-[10px] text-zinc-600 mt-3">
                    Plan source: {plan.source === 'llm' ? 'AI' : 'rules engine'}
                  </p>
                </div>
              )}

              {findings.length > 0 && (
                <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="ai-seo-findings">
                  <h3 className="text-base font-semibold inline-flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    Findings ({findings.length})
                  </h3>
                  <ul className="space-y-2">
                    {findings.map((f) => (
                      <li key={f.code + (f.title || '')} className="rounded-lg border border-zinc-800 px-3 py-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium">{f.title}</span>
                          <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${SEVERITY_TONE[f.severity] || SEVERITY_TONE.medium}`}>
                            {f.severity}
                          </span>
                        </div>
                        <p className="text-xs text-zinc-400 mt-1">{f.detail}</p>
                        {(f.pages || []).length > 0 && (
                          <p className="text-[11px] text-zinc-600 mt-1 truncate">
                            {(f.pages || []).slice(0, 3).join(' · ')}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiSeoDashboard;
